import {
  getReturnSpeedConstraints,
  getWaypointSpeedConstraints,
  WAYPOINT_GIMBAL_PITCH,
  WAYPOINT_HEADING,
  WAYPOINT_HEIGHT,
  WAYPOINT_PAUSE_TIME,
} from "./constraints";

import type { FlightMode, VehicleConfiguration } from "./constraints";

/**
 * Mission Editor Validation Functions
 *
 * This file contains validation logic for mission editing.
 * These validators are shared between cloud web and mobile platforms.
 */

/**
 * Mission details for validation
 */
export interface MissionDetails {
  missionName?: string;
  siteId?: string;
  takeoffPointId?: string;
  vehicleConfiguration?: VehicleConfiguration;
  templateUuid?: string; // UUID of the mission template (for updates)
}

/**
 * Waypoint data for validation
 */
export interface WaypointData {
  height: number;
  speed: number;
  heading: number;
  gimbalPitch: number;
  pauseTime: number;
  latitude?: number;
  longitude?: number;
}

/**
 * Return behavior data for validation
 */
export interface ReturnBehaviorData {
  returnSpeed: number;
}

/**
 * Validate mission details step
 *
 * @returns Array of error messages (empty if valid)
 */
export function validateMissionDetails(details: MissionDetails): string[] {
  const errors: string[] = [];

  // If site is selected, must have takeoff point
  if (details.siteId) {
    if (!details.takeoffPointId) {
      errors.push("Select a dock");
    }
  } else {
    // If no site, must have vehicle configuration
    if (!details.vehicleConfiguration || details.vehicleConfiguration.vehicleType === "UNKNOWN") {
      errors.push("Select a device");
    }
  }

  // Mission name is optional but could add validation if needed
  if (details.missionName && details.missionName.trim().length === 0) {
    errors.push("Mission name cannot be empty");
  }

  return errors;
}

/**
 * Validate waypoint data
 *
 * @param waypoint - Waypoint to validate
 * @param flightMode - Current flight mode (nominal or twilight)
 * @param vehicleConfig - Vehicle configuration for speed limits
 * @returns Array of error messages (empty if valid)
 */
export function validateWaypoint(
  waypoint: WaypointData,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): string[] {
  const errors: string[] = [];

  // Validate height
  if (waypoint.height < WAYPOINT_HEIGHT.MIN || waypoint.height > WAYPOINT_HEIGHT.MAX) {
    errors.push(
      `Height must be between ${WAYPOINT_HEIGHT.MIN.toFixed(1)}m and ${WAYPOINT_HEIGHT.MAX.toFixed(1)}m`
    );
  }

  // Validate speed
  const speedConstraints = getWaypointSpeedConstraints(flightMode, vehicleConfig);
  if (waypoint.speed < speedConstraints.MIN || waypoint.speed > speedConstraints.MAX) {
    errors.push(`Speed must be between ${speedConstraints.MIN}m/s and ${speedConstraints.MAX}m/s`);
  }

  // Validate heading
  if (waypoint.heading < WAYPOINT_HEADING.MIN || waypoint.heading > WAYPOINT_HEADING.MAX) {
    errors.push(`Heading must be between ${WAYPOINT_HEADING.MIN}° and ${WAYPOINT_HEADING.MAX}°`);
  }

  // Validate gimbal pitch
  if (
    waypoint.gimbalPitch < WAYPOINT_GIMBAL_PITCH.MIN ||
    waypoint.gimbalPitch > WAYPOINT_GIMBAL_PITCH.MAX
  ) {
    errors.push(
      `Gimbal pitch must be between ${WAYPOINT_GIMBAL_PITCH.MIN}° and ${WAYPOINT_GIMBAL_PITCH.MAX}°`
    );
  }

  // Validate pause time
  if (
    waypoint.pauseTime < WAYPOINT_PAUSE_TIME.MIN ||
    waypoint.pauseTime > WAYPOINT_PAUSE_TIME.MAX
  ) {
    errors.push(
      `Pause time must be between ${WAYPOINT_PAUSE_TIME.MIN}s and ${WAYPOINT_PAUSE_TIME.MAX}s`
    );
  }

  // Validate lat/lon if provided
  if (waypoint.latitude !== undefined) {
    if (waypoint.latitude < -90 || waypoint.latitude > 90) {
      errors.push("Latitude must be between -90° and 90°");
    }
  }

  if (waypoint.longitude !== undefined) {
    if (waypoint.longitude < -180 || waypoint.longitude > 180) {
      errors.push("Longitude must be between -180° and 180°");
    }
  }

  return errors;
}

/**
 * Validate return behavior
 *
 * @param returnBehavior - Return behavior to validate
 * @param flightMode - Current flight mode (nominal or twilight)
 * @param vehicleConfig - Vehicle configuration for speed limits
 * @returns Array of error messages (empty if valid)
 */
export function validateReturnBehavior(
  returnBehavior: ReturnBehaviorData,
  flightMode: FlightMode = "nominal",
  vehicleConfig?: VehicleConfiguration
): string[] {
  const errors: string[] = [];

  const speedConstraints = getReturnSpeedConstraints(flightMode, vehicleConfig);

  if (
    returnBehavior.returnSpeed < speedConstraints.MIN ||
    returnBehavior.returnSpeed > speedConstraints.MAX
  ) {
    errors.push(
      `Return speed must be between ${speedConstraints.MIN}m/s and ${speedConstraints.MAX}m/s`
    );
  }

  return errors;
}

/**
 * Validate entire mission (all steps)
 *
 * @returns Object with errors for each step
 */
export interface MissionValidationResult {
  details: string[];
  waypoints: Record<number, string[]>; // waypoint index -> errors
  returnBehavior: string[];
}

export function validateMission(
  details: MissionDetails,
  waypoints: WaypointData[],
  returnBehavior: ReturnBehaviorData,
  flightMode: FlightMode = "nominal"
): MissionValidationResult {
  const result: MissionValidationResult = {
    details: validateMissionDetails(details),
    waypoints: {},
    returnBehavior: validateReturnBehavior(
      returnBehavior,
      flightMode,
      details.vehicleConfiguration
    ),
  };

  // Validate each waypoint
  waypoints.forEach((waypoint, index) => {
    const errors = validateWaypoint(waypoint, flightMode, details.vehicleConfiguration);
    if (errors.length > 0) {
      result.waypoints[index] = errors;
    }
  });

  return result;
}

/**
 * Check if mission is valid (no errors)
 */
export function isMissionValid(validationResult: MissionValidationResult): boolean {
  if (validationResult.details.length > 0) return false;
  if (validationResult.returnBehavior.length > 0) return false;
  if (Object.keys(validationResult.waypoints).length > 0) return false;
  return true;
}
